import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentResult({ kind }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(kind === "cancel" ? "cancelled" : "polling");
  const [bidId, setBidId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (kind !== "success" || !sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.bid_request_id) setBidId(data.bid_request_id);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 10) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [kind, sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="payment-result-page">
      {state === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#2563EB] animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-2">{t("Confirmando tu pago...")}</h1>
          <p className="text-sm text-slate-400">{t("Estamos verificando tu depósito con Stripe. No cierres esta página.")}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#22C55E] mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-2" data-testid="payment-success-title">{t("¡Depósito confirmado!")}</h1>
          <p className="text-sm text-slate-400 mb-8">{t("Tu solicitud pasó a \"Depósito pagado\". Nuestro equipo comenzará a preparar tu puja.")}</p>
          <Link to={bidId ? `/panel/solicitud/${bidId}` : "/panel"}>
            <Button data-testid="payment-success-cta" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">
              {t("Ver mi solicitud")}
            </Button>
          </Link>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-2">{t("Pago en verificación")}</h1>
          <p className="text-sm text-slate-400 mb-8">{t("El pago está siendo procesado. Revisa tu panel en unos minutos.")}</p>
          <Link to="/panel"><Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8">{t("Ir a mi panel")}</Button></Link>
        </>
      )}
      {state === "cancelled" && (
        <>
          <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-2" data-testid="payment-cancel-title">{t("Pago cancelado")}</h1>
          <p className="text-sm text-slate-400 mb-8">{t("No se realizó ningún cargo. Puedes reintentar el pago del depósito desde tu solicitud.")}</p>
          <Link to="/panel">
            <Button data-testid="payment-cancel-cta" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 hover:text-white rounded-full px-8">
              {t("Volver a mi panel")}
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}
